"use client"
import { Dialog, Transition } from "@headlessui/react"
import { Menu, X } from "lucide-react"
import { Fragment, useEffect, useRef, useState } from "react"

interface DrawerProps {
  isOpen?: boolean
  onClose?: () => void
  header?: React.ReactNode
  content?: React.ReactNode
  footer?: React.ReactNode
  triggerButton?: boolean
  triggerButtonIcon?: React.ReactNode
}

const DrawerCustom = ({
  isOpen: controlledIsOpen,
  onClose,
  header,
  content,
  footer,
  triggerButton = false,
  triggerButtonIcon,
}: DrawerProps) => {
  const drawerRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const [internalIsOpen, setInternalIsOpen] = useState(false)

  const closeDrawer = () => setIsOpen(false)

  // Kiểm tra isOpen có được điều khiển từ ngoài không
  const isOpen = controlledIsOpen ?? internalIsOpen

  // Hàm mở/đóng Drawer
  const setIsOpen = (state: boolean) => {
    if (controlledIsOpen === undefined) {
      setInternalIsOpen(state)
    }
    if (!state && onClose) {
      onClose()
    }
  }

  // Xử lý focus tự động
  useEffect(() => {
    if (isOpen) {
      drawerRef.current?.focus()
    } else {
      triggerRef.current?.focus()
    }
  }, [isOpen])

  return (
    <>
      {/* Nút mở Drawer */}
      {triggerButton && (
        <button
          aria-label="Open drawer"
          ref={triggerRef}
          onClick={() => setIsOpen(true)}
          className="rounded-md focus:outline-none"
        >
          {triggerButtonIcon || <Menu />}
        </button>
      )}

      <Transition show={isOpen}>
        <Dialog onClose={closeDrawer} className="relative z-50">
          <Transition.Child
            as={Fragment}
            enter="transition-all ease-in-out duration-300"
            enterFrom="opacity-0 backdrop-blur-none"
            enterTo="opacity-100 backdrop-blur-[.5px]"
            leave="transition-all ease-in-out duration-200"
            leaveFrom="opacity-100 backdrop-blur-[.5px]"
            leaveTo="opacity-0 backdrop-blur-none"
          >
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition-all ease-in-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition-all ease-in-out duration-200"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel
              className={`fixed bottom-0 right-0 top-0 flex h-full flex-col border-l border-neutral-200 bg-white text-black backdrop-blur-xl dark:border-neutral-700 dark:bg-black/80 dark:text-white w-full sm:w-fit min-w-[350px] max-w-[650px]`}
            >
              {/* Header */}
              <div className="p-4 border-b flex justify-between items-center">
                <div>{header || "Header"}</div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-600 hover:text-black"
                >
                  <X size={24} />
                </button>
              </div>
              {/* Nội dung Drawer */}
              <div className="flex-1 p-2 overflow-auto">{content}</div>
              {/* Footer */}
              <div className="border-t">{footer}</div>
            </Dialog.Panel>
          </Transition.Child>
        </Dialog>
      </Transition>
    </>
  )
}

export default DrawerCustom
